/**
 * ParticipantImpactTab.tsx — VER-59
 *
 * Read-only per-scale pre/post impact view for ONE participant. Reads native
 * SurveyResponse-derived scores via getParticipantImpact and renders a card per
 * scale with a pre/post bar chart, the two scores, the raw delta, and a
 * polarity-aware direction label (Improved / Declined / No change — respects
 * `higherIsBetter`, mirroring changeDirection() in assess/report/reportFormat.ts).
 *
 * VER-118 adds the S2–S5 mood line. It renders in its OWN card below the scale cards, on its own
 * 1–5 axis, and is never given a scale card of its own: mood is a single holistic rating, and
 * presenting it beside validated instruments in the same visual form would invite it to be read
 * as one. Its absence never blocks the clinical section, and vice versa.
 *
 * Read-only. No fidelity content.
 */
import React, { FC, useEffect, useState } from "react";
import { Box, Chip, Grid, Stack, Typography, useTheme } from "@mui/material";
import { SvgIconComponent, TrendingDown, TrendingFlat, TrendingUp } from "@mui/icons-material";
import { VictoryAxis, VictoryBar, VictoryChart, VictoryContainer, VictoryTheme } from "victory";
import dayjs from "dayjs";
import Spinner from "../layout/Spinner";
import { Error } from "../../core/components/Error";
import { SimpleBlankSlate } from "../../core/components/SimpleBlankSlate";
import { VCard } from "../../core/components/VCard";
import { getParticipantImpact, ParticipantMoodSeries, ParticipantScaleImpact } from "../../client/api/survey";
import { MoodLineChart } from "../assess/impact/MoodLineChart";

type ParticipantImpactTabProps = {
    participantId: number;
};

type ImpactDirection = "improved" | "declined" | "unchanged";

/**
 * Wellbeing direction of a raw delta, respecting scale polarity. Mirrors
 * changeDirection() in assess/report/reportFormat.ts: on a lower-is-better scale a
 * fall in score is an improvement, so the sign of the delta alone is not enough.
 */
const scaleDirection = (delta: number, higherIsBetter: boolean): ImpactDirection => {
    if (delta === 0) return "unchanged";
    const improved = higherIsBetter ? delta > 0 : delta < 0;
    return improved ? "improved" : "declined";
};

const DIRECTION_META: Record<
    ImpactDirection,
    { label: string; color: "success" | "error" | "default"; Icon: SvgIconComponent }
> = {
    improved: { label: "Improved", color: "success", Icon: TrendingUp },
    declined: { label: "Declined", color: "error", Icon: TrendingDown },
    unchanged: { label: "No change", color: "default", Icon: TrendingFlat },
};

const formatDate = (iso: string): string => dayjs(iso).format("D MMM YYYY");

const formatDelta = (delta: number): string => (delta > 0 ? `+${delta}` : `${delta}`);

type ProgrammeImpactGroup = {
    programmeId: number;
    programmeName: string;
    scales: ParticipantScaleImpact[];
};

/**
 * Group scales by programme, preserving first-seen order. A participant in several
 * programmes gets one section per programme — pre/post pairs are never merged across
 * programmes, which would misrepresent the change on any single one.
 */
const groupByProgramme = (scales: ParticipantScaleImpact[]): ProgrammeImpactGroup[] => {
    const groups: ProgrammeImpactGroup[] = [];
    const indexByProgramme = new Map<number, number>();

    for (const scale of scales) {
        let position = indexByProgramme.get(scale.programmeId);
        if (position === undefined) {
            position = groups.length;
            indexByProgramme.set(scale.programmeId, position);
            groups.push({ programmeId: scale.programmeId, programmeName: scale.programmeName, scales: [] });
        }
        groups[position].scales.push(scale);
    }

    return groups;
};

/** Two-bar pre/post chart for a single scale. Colours from theme (info = pre, primary = post). */
const ScalePrePostChart: FC<{ pre: number; post: number; scaleName: string }> = ({ pre, post, scaleName }) => {
    const theme = useTheme();
    const colorPre = theme.palette.info.main;
    const colorPost = theme.palette.primary.main;
    const fontFamily = theme.typography.fontFamily;
    const yMax = Math.max(pre, post);

    return (
        <VictoryChart
            theme={VictoryTheme.material}
            containerComponent={<VictoryContainer responsive={true} title={`${scaleName} — pre and post scores`} />}
            height={220}
            domainPadding={{ x: 60 }}
            padding={{ top: 16, bottom: 32, left: 40, right: 16 }}
            domain={{ y: [0, yMax > 0 ? yMax * 1.15 : 1] }}
        >
            <VictoryAxis style={{ tickLabels: { fontSize: 10, fontFamily } }} />
            <VictoryAxis dependentAxis style={{ tickLabels: { fontSize: 10, fontFamily } }} />
            <VictoryBar
                barWidth={40}
                data={[
                    { x: "Pre", y: pre },
                    { x: "Post", y: post },
                ]}
                style={{ data: { fill: ({ datum }) => (datum.x === "Pre" ? colorPre : colorPost) } }}
            />
        </VictoryChart>
    );
};

/** One labelled score with its administration date. Renders an em-dash when absent. */
const ScoreLine: FC<{ label: string; score: number | null; date: string | null }> = ({ label, score, date }) => (
    <Box>
        <Typography variant="body2" color="textSecondary">
            {label}
        </Typography>
        <Typography variant="h6">{score === null ? "—" : score}</Typography>
        {date && (
            <Typography variant="caption" color="textSecondary">
                {formatDate(date)}
            </Typography>
        )}
    </Box>
);

const ScaleImpactCard: FC<{ scale: ParticipantScaleImpact }> = ({ scale }) => {
    const hasPost = scale.post !== null && scale.delta !== null;
    const direction = hasPost ? scaleDirection(scale.delta as number, scale.higherIsBetter) : null;
    const meta = direction ? DIRECTION_META[direction] : null;

    return (
        <Box mb={3}>
            <VCard title={scale.scaleName} variant="outlined">
                <Grid container spacing={3} alignItems="center">
                    <Grid item xs={12} md={7}>
                        {scale.pre && scale.post ? (
                            <ScalePrePostChart
                                pre={scale.pre.score}
                                post={scale.post.score}
                                scaleName={scale.scaleName}
                            />
                        ) : (
                            <Box display="flex" alignItems="center" justifyContent="center" minHeight={120} px={2}>
                                <Typography variant="body2" color="textSecondary" textAlign="center">
                                    Baseline recorded — awaiting endpoint administration.
                                </Typography>
                            </Box>
                        )}
                    </Grid>
                    <Grid item xs={12} md={5}>
                        <Stack spacing={2}>
                            <ScoreLine
                                label="Pre"
                                score={scale.pre?.score ?? null}
                                date={scale.pre?.administeredAt ?? null}
                            />
                            <ScoreLine
                                label="Post"
                                score={scale.post?.score ?? null}
                                date={scale.post?.administeredAt ?? null}
                            />
                            {hasPost && meta ? (
                                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                    <Typography variant="body2" color="textSecondary">
                                        Change
                                    </Typography>
                                    <Typography variant="h6">{formatDelta(scale.delta as number)}</Typography>
                                    <Chip
                                        size="small"
                                        color={meta.color}
                                        icon={<meta.Icon fontSize="small" />}
                                        label={meta.label}
                                    />
                                </Box>
                            ) : (
                                <Typography variant="body2" color="textSecondary">
                                    A post score is needed before impact can be measured.
                                </Typography>
                            )}
                        </Stack>
                    </Grid>
                </Grid>
            </VCard>
        </Box>
    );
};

/**
 * This participant's own mood line for one programme (VER-118).
 *
 * The caption is overridden because MoodLineChart's default says individual responses are not
 * shown — true on the group view it was built for, false here. This tab deliberately surfaces one
 * participant's own data to a viewer already permitted to see it, so the copy must say so.
 */
const ParticipantMoodCard: FC<{ series: ParticipantMoodSeries }> = ({ series }) => (
    <Box mb={3}>
        <VCard title="Mood" variant="outlined">
            <MoodLineChart
                title="In-session mood (sessions 2–5)"
                caption="This participant's own mood rating per session (1 = very low, 5 = very high). Captured during delivery — not a validated clinical scale, and not compared with the survey scores above."
                series={series.points.map((point) => ({
                    sessionLabel: `S${point.sessionNumber}`,
                    groupMoodAvg: point.score,
                }))}
            />
        </VCard>
    </Box>
);

const ParticipantImpactTab: FC<ParticipantImpactTabProps> = ({ participantId }) => {
    const [scales, setScales] = useState<ParticipantScaleImpact[]>([]);
    const [mood, setMood] = useState<ParticipantMoodSeries[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        if (Number.isNaN(participantId)) {
            setError(true);
            setLoading(false);
            return;
        }

        setLoading(true);
        getParticipantImpact(participantId)
            .then((data) => {
                setScales(data.scales);
                // Tolerate an older response shape rather than crashing the clinical section.
                setMood(data.mood ?? []);
                setError(false);
            })
            .catch(() => setError(true))
            .finally(() => setLoading(false));
    }, [participantId]);

    if (loading) {
        return <Spinner />;
    }

    if (error) {
        return <Error description="Unable to load impact scores" showImage={false} />;
    }

    // The blank slate applies only when there is NOTHING to show. Mood alone is still something:
    // a participant checked in every session but has no survey yet is not an empty page.
    if (scales.length === 0 && mood.length === 0) {
        return (
            <SimpleBlankSlate
                header="No impact scores yet"
                content="Wellbeing scores appear here once this participant has completed a survey. Pre and post results are then compared automatically."
            />
        );
    }

    const moodByProgramme = new Map(mood.map((series) => [series.programmeId, series]));
    // A programme may have mood but no scored survey yet — it still deserves a section.
    const scaleGroups = groupByProgramme(scales);
    const moodOnlyProgrammes = mood.filter(
        (series) => !scaleGroups.some((group) => group.programmeId === series.programmeId),
    );

    return (
        <Box>
            {scaleGroups.map((group) => (
                <Box key={group.programmeId} mb={4}>
                    <Typography variant="h6" component="h2" gutterBottom>
                        {group.programmeName}
                    </Typography>
                    {group.scales.map((scale) => (
                        <ScaleImpactCard key={`${scale.programmeId}:${scale.scaleCode}`} scale={scale} />
                    ))}
                    {moodByProgramme.has(group.programmeId) ? (
                        <ParticipantMoodCard series={moodByProgramme.get(group.programmeId) as ParticipantMoodSeries} />
                    ) : null}
                </Box>
            ))}
            {/*
             * A programme with mood readings but no survey scores has no scale group, so it gets its
             * heading here. Without it the card floats unlabelled and the reader cannot tell which
             * programme the line belongs to — worse when a participant is on more than one.
             */}
            {moodOnlyProgrammes.map((series) => (
                <Box key={`mood-${series.programmeId}`} mb={4}>
                    {series.programmeName ? (
                        <Typography variant="h6" component="h2" gutterBottom>
                            {series.programmeName}
                        </Typography>
                    ) : null}
                    <ParticipantMoodCard series={series} />
                </Box>
            ))}
        </Box>
    );
};

export default ParticipantImpactTab;
